import { CustomMetadata } from "types";
import { useCustomMetadataModal } from "hooks/useCustomMetadataModal";
import { useFieldUniqueValues } from "hooks/CustomMetadata/useFieldUniqueValues";
import { useModalOperatorsAndValues } from "hooks/CustomMetadata/useModalOperatorsAndValues";
import QueryModalInput from "views/components/QueryModal/QueryModalInput";
import { CustomNameStringArrayIcon } from "assets/icons/CustomMetadataIcons";
import BaseQueryModal from "./BaseQueryModal";
import { useMultiSelectQueryModal } from "hooks/UseQueryModal/useMultiSelectQueryModal";

interface StringArrayModalProps {
  field: CustomMetadata;
  editQueryId?: string;
}

export default function StringArrayModal({
  editQueryId,
  field,
}: StringArrayModalProps) {
  const { modalRef, handleSubmission, fieldName } = useCustomMetadataModal({
    field,
    editQueryId,
  });

  // Fetch unique values from API
  const { values: apiValues } = useFieldUniqueValues({
    fieldId: field.field_id,
  });

  const {
    operator,
    setOperator,
    operatorOptions,
    values,
    setValues,
    selectedIds,
    searchText,
    setSearchText,
    previousValue,
    previousOperator,
    existingQuery,
  } = useModalOperatorsAndValues({
    type: "string_array",
    field,
    editQueryId,
    options: apiValues || [],
  });

  const previousValueIds = previousValue
    ? values.filter((v) => previousValue.includes(v.text)).map((v) => v.id)
    : [];

  const { isSubmitDisabled } = useMultiSelectQueryModal({
    selectedIds,
    previousIds: previousValueIds,
    selectedOperator: operator,
    initialOperator: previousOperator,
    editQueryId,
    existingQueryId: existingQuery?.id,
  });

  const handleSubmit = () => {
    if (isSubmitDisabled) return;
    const selectedValues = values.filter((v) => v.selected).map((v) => v.text);
    handleSubmission(operator, selectedValues);
  };

  const operators = {
    options: operatorOptions,
    selected: operator,
    onSelect: setOperator,
  };

  const handleRemoveValue = (id: number | string) => {
    setValues(values.map((v) => (v.id === id ? { ...v, selected: false } : v)));
  };

  const handleSelectValue = (id: number | string) => {
    setValues(
      values.map((v) => (v.id === id ? { ...v, selected: !v.selected } : v)),
    );
  };

  const handleInputChange = (value: string) => {
    setSearchText(value);
  };

  return (
    <BaseQueryModal<typeof operator>
      title={fieldName}
      keepOpenOnLoading
      modalRef={modalRef}
      icon={<CustomNameStringArrayIcon />}
      isSubmitDisabled={isSubmitDisabled}
      onSubmit={handleSubmit}
      operators={operators}
    >
      <QueryModalInput
        mode="multiple"
        options={values}
        searchText={searchText}
        selected={selectedIds}
        onRemove={handleRemoveValue}
        onSelect={handleSelectValue}
        onInputChange={handleInputChange}
      />
    </BaseQueryModal>
  );
}
